import { Pencil, Search } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ListCard } from "@/components/ui/list-card";
import { Tag } from "@/components/ui/tag";

import type { FsNode } from "../../caseFiles/types";
import { nodeFilename, nodeTitle } from "../../caseFiles/nodeNames";

export const MAX_FIELD_FILES = 20;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  actionLabel: string;
  /** Most files that can be checked; `null` for no cap. */
  limit?: number | null;
  nodes: FsNode[];
  suggested: string[];
  /** Picked last time; preselected over the suggestions. */
  previous: string[];
  onFill: (nodeIds: string[]) => void;
  /** Makes each file's category tag open the category and type editor. */
  onEditCategory?: (node: FsNode) => void;
};

type Group = { key: string; title: string; files: FsNode[] };

/** The files a picker offers: never folders, deleted or internal files. */
export const pickableFiles = (nodes: FsNode[]): FsNode[] =>
  nodes.filter(
    (n) => n.kind === "file" && !n.is_deleted && n.document_role !== "internal"
  );

/** Last time's files, else the suggestions when they all fit, else none. */
export const initialPicks = (
  previous: string[],
  suggested: string[],
  limit: number | null = MAX_FIELD_FILES
): string[] => {
  if (previous.length)
    return limit === null ? previous : previous.slice(0, limit);
  return limit === null || suggested.length <= limit ? suggested : [];
};

/**
 * Picks the case files Draft Strategy questions are filled from. Reads the
 * tree the IDE already holds; internal files are never offered.
 */
export const FillFromFilesDialog: FC<Props> = ({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  limit = MAX_FIELD_FILES,
  nodes,
  suggested,
  previous,
  onFill,
  onEditCategory
}) => {
  const [picked, setPicked] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const files = useMemo(() => pickableFiles(nodes), [nodes]);
  useEffect(() => {
    if (!open) return;
    const known = new Set(files.map((n) => n.id));
    setPicked(
      initialPicks(
        previous.filter((id) => known.has(id)),
        suggested.filter((id) => known.has(id)),
        limit
      )
    );
    setQuery("");
    // Not on `files`: a tree refresh must not undo the attorney's checks.
  }, [open, previous, suggested, limit]);
  const full = limit !== null && picked.length >= limit;

  const groups = useMemo<Group[]>(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const needle = query.trim().toLowerCase();
    const match = (n: FsNode) =>
      !needle || nodeTitle(n).toLowerCase().includes(needle);
    const suggestedSet = new Set(suggested);
    const out: Group[] = [];
    const top = files.filter((n) => suggestedSet.has(n.id) && match(n));
    if (top.length)
      out.push({
        key: "__suggested__",
        title:
          limit !== null && suggested.length > limit
            ? `Suggested · ${suggested.length}, Pick Up to ${limit}`
            : "Suggested",
        files: top
      });
    const folders = new Map<string, Group>();
    for (const n of files.filter((f) => !suggestedSet.has(f.id) && match(f))) {
      const parent = n.parent_id ? byId.get(n.parent_id) : undefined;
      const key = parent?.id ?? "__root__";
      const group = folders.get(key) ?? {
        key,
        title: parent ? nodeTitle(parent) : "Case Files",
        files: []
      };
      group.files.push(n);
      folders.set(key, group);
    }
    return [...out, ...folders.values()];
  }, [nodes, files, suggested, query, limit]);

  const suggestedSet = useMemo(() => new Set(suggested), [suggested]);
  const toggle = (id: string) =>
    setPicked((p) =>
      p.includes(id)
        ? p.filter((x) => x !== id)
        : limit !== null && p.length >= limit
        ? p
        : [...p, id]
    );
  const allPicked = files.length > 0 && picked.length === files.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-160 flex-col sm:max-w-140">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-fg-4" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search case files"
            className="pl-7"
            aria-label="Search case files"
          />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-px">
          {groups.length ? (
            groups.map((group) => (
              <div key={group.key} className="flex flex-col gap-1.5">
                <span className="text-sm text-fg-3">{group.title}</span>
                <ListCard>
                  {group.files.map((n) => {
                    const on = picked.includes(n.id);
                    return (
                      <label
                        key={n.id}
                        htmlFor={`fill-file-${n.id}`}
                        className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-fill-1"
                      >
                        <Checkbox
                          id={`fill-file-${n.id}`}
                          checked={on}
                          onCheckedChange={() => toggle(n.id)}
                          disabled={!on && full}
                        />
                        <FileTypeIcon name={nodeFilename(n)} />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-base text-fg">
                            {nodeTitle(n)}
                          </span>
                          <span className="text-sm text-fg-3">
                            {n.page_count
                              ? `${n.page_count} ${
                                  n.page_count === 1 ? "page" : "pages"
                                }`
                              : "Not read yet"}
                            {suggestedSet.has(n.id) &&
                            group.key !== "__suggested__"
                              ? " · Suggested"
                              : ""}
                          </span>
                        </span>
                        {onEditCategory ? (
                          <button
                            type="button"
                            className="shrink-0 rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                            aria-label={`Change category of ${nodeTitle(n)}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onEditCategory(n);
                            }}
                          >
                            <Tag className="cursor-pointer hover:bg-fill-3">
                              {n.taxonomy_category ?? "Uncategorized"}
                              <Pencil />
                            </Tag>
                          </button>
                        ) : (
                          <Tag>{n.taxonomy_category ?? "Uncategorized"}</Tag>
                        )}
                      </label>
                    );
                  })}
                </ListCard>
              </div>
            ))
          ) : (
            <span className="py-6 text-center text-sm text-fg-3">
              No case files match.
            </span>
          )}
        </div>
        <DialogFooter className="items-center">
          <span className="flex flex-1 items-center gap-2 text-sm text-fg-3">
            {limit === null
              ? `${picked.length} Selected`
              : `${picked.length} Selected · Up to ${limit}`}
            {limit === null && files.length ? (
              <Button
                variant="link"
                size="xs"
                onClick={() =>
                  setPicked(allPicked ? [] : files.map((n) => n.id))
                }
              >
                {allPicked ? "Clear" : "Select All"}
              </Button>
            ) : null}
          </span>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!picked.length}
            onClick={() => onFill(picked)}
          >
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
